'use client'
import { useLaunchGame } from '@/apiUtilities/mutations/useLaunchGame'
import WindowsModal from '@/app/[locale]/games/[id]/modals/WindowsModal/WindowsModal'
import { API_Data_Launch_Game_Response, LaunchGameData } from '@/interfaces/api/games/api_request_types'
import { ModalTypes } from '@/interfaces/api/generic/api_modals_types'
import { useExecutionStore } from '@/stores/useExecutionStore'
import { useModalStore } from '@/stores/useModalStore'
import { useQueueStore } from '@/stores/useQueueStore'
import { useUserStore } from '@/stores/useUserStore'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { getHTML5ConfigParams } from '@/utils/functions'
import { GameType } from '@/interfaces/games_types'
import { ProfileType } from '@/interfaces/profile_types'
import QueueModal from './Modals/QueueModal/QueueModal'
import Notification from './Notification/Notification'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

export default function QueueManager() {
  const queryClient = useQueryClient()
  const te = useTranslations('Errors.GameLaunch')
  const { profileId } = useUserStore()
  const { openModal, closeModal } = useModalStore()
  const { getQueue, getTimer, setTimer, updateQueue, removeQueue, setQueue } = useQueueStore()
  const queue = profileId ? getQueue(profileId) : undefined
  const timer = profileId ? getTimer(profileId) : 0
  const launchGame = useLaunchGame()
  const router = useRouter()
  const { setCooldown, setExecution } = useExecutionStore()
  const locale = useLocale()

  /**
   * Handles the succesful launch of an Epic game
   * 
   * @param response Response from the API when a game is successfully launched
   */
  function handleLaunchEpicGameSuccess(response: API_Data_Launch_Game_Response) {
    openModal({
      type: ModalTypes.NATIVE,
      content: <WindowsModal callback={() => closeModal()} />
    })	
    window.location.href=response.exec_url
  }

  /**
   * Handles the succesful launch of a non-Epic game
   * 
   * @param response Response from the API when a game is successfully launched
   * @param profile Profile of the user launching the game
   * @param game Information of the game being launched
   */
  function handleLaunchGameSuccess(response: API_Data_Launch_Game_Response, profile: ProfileType, game: GameType) {
    const execUrl = response.exec_url
    const params = getHTML5ConfigParams(execUrl, profile, game)
    router.push(`/html5?${params}`)
  }

  /**
	 * Handles the error of launching a game
	 * Shows a queue modal if the error is a queue error,
	 * otherwise shows a toast with the corresponding error message
	 * 
	 * @param error Error returned by the API when trying to launch a game
	 * @param profile Profile of the user launching the game
	 * @param game Information of the game being launched
	 */
	function handleLaunchGameError(error: any, profile: ProfileType, game: GameType) {
    // Detect queue error
    const queueErr = error?.error?.errors?.find((e: any) => e.string === 'IrisErrnoUserHasBeenEnqueued');
    if (queueErr && error?.error?.data?.position && error.error?.data?.retry_in) {
      setQueue({
        profile: profile!,
        game: game,
        position: error.error.data.position,
        retryIn: error.error.data.retry_in,
        lastTried: Date.now(),
      })
      setTimer(profile.id, error.error.data.retry_in)

      /* openModal({
        content: <QueueModal onClose={closeModal}/>
      }) */
    } else {
      switch(error.error.errors[0].errno) {
        case 178: 
          toast.error(<Notification text={te('multipleDevicesError')} />)
          setCooldown(30)
          break
        case 179:
          toast.error(<Notification text={te('genericGameLaunchError')} />)
          if (profileId) {
            setExecution(profileId, Date.now())
            setCooldown(30)
          }
          break
        case 105:
          toast.error(<Notification text={te('subscriptionExpired')} />)
          queryClient.invalidateQueries({ queryKey: ['self'] })
          queryClient.invalidateQueries({ queryKey: ['profiles'] })
          queryClient.invalidateQueries({ queryKey: ['games'] })
          queryClient.invalidateQueries({ queryKey: ['game'] })
          break
        case 188:
          toast.error(<Notification text={te('noTimeLeft')} />)
          break
        case 299:
          error.error.errors[0].data.description === '0' ? 
          toast.error(<Notification text={te('updatingError')} />) :
          toast.error(<Notification text={te('maintenance')} />)

          queryClient.invalidateQueries({ queryKey: ['game'] })
          break
        case 307:
          toast.error(<Notification text={te('anotherProfilePlaying')} />)
          break
        default:
          toast.error(<Notification text={te('defaultUnknowError')} />)
          if (profileId) {
            setExecution(profileId, Date.now())
            setCooldown(30)
          }
          break
      }
    }
  }

  useEffect(() => {
    if (!queue) return
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(queue.profile.id, Math.max(getTimer(queue.profile.id) - 1, 0))
      }, 1000);
      return () => clearInterval(interval)
    }
  }, [queue, timer, setTimer, getTimer])

  useEffect(() => {
    if (!queue || timer > 0) return
    (async () => {
      try {
        const gameData = { 
          data: {
            client_id: "WebBrowser",
            region_code: 'ES',
            lang_code: locale,
            entry_id: queue.game.game_id
          } 
        } as LaunchGameData
        
        launchGame.mutate({ profileId: profileId!, payload: gameData },
          {
            onSuccess: (response: API_Data_Launch_Game_Response) => {
                // Save execution time
                if (profileId) setExecution(profileId, Date.now())
                // Clear queue if exists
                if (profileId) removeQueue(profileId)
                if(queue.game.type === 'epic') handleLaunchEpicGameSuccess(response)
					      else handleLaunchGameSuccess(response, queue.profile, queue.game)
            },
            onError: (error: any) => handleLaunchGameError(error, queue.profile, queue.game)
          }
        )
      } catch (err: any) {
        if (err?.data?.position && err?.data?.retry_in) {
          updateQueue(queue.profile, {
            position: err.data.position,
            retryIn: err.data.retry_in,
            lastTried: Date.now(),
          })
        }
      }
    })()
  }, [timer])

  return null
}
